import React, { useMemo, useState } from "react";
import moment from "moment";
import CustomisedModal from "../../../../components/Modal/CustomisedModal";
import { API_DATE_FORMAT, longStamp, statusSuffix } from "../ticketUtils";

/* Newest first, all the way down: what happened last, then every status change
   behind it, and the ticket's own beginning at the bottom. */
const FILTERS = [
    { key: "all", label: "All" },
    { key: "status", label: "Status changes" },
    { key: "forward", label: "Forwards" },
];

const ActivitiesModal = ({ isOpen, ticket, comments = [], loading = false, onClose }) => {
    const [filter, setFilter] = useState("all");

    const counts = useMemo(
        () => ({
            all: comments.length,
            status: comments.filter((entry) => entry.kind !== "forward").length,
            forward: comments.filter((entry) => entry.kind === "forward").length,
        }),
        [comments]
    );

    const shown = useMemo(
        () =>
            filter === "all"
                ? comments
                : comments.filter((entry) =>
                      filter === "forward"
                          ? entry.kind === "forward"
                          : entry.kind !== "forward"
                  ),
        [comments, filter]
    );

    /* Newest first: whatever happened last is the first thing read.

       That also fixes where the ticket's own beginning goes - it is the oldest
       event there is, so it belongs at the bottom, and it is pinned there.
       Keeping it at the top while the rest ran newest-first gave two orders in
       one list: oldest, then newest, then backwards.

       "Last updated" is sorted in with the rest rather than pinned. It is a
       dated event like any other, and pinning it put it above changes that
       happened after it.

       Stamps only carry minutes, so two things in the same minute compare
       equal. The sort is left stable on purpose, which keeps the order the API
       already worked out from its full-second timestamps; the one tie broken
       here is "Last updated", which sits directly above the change it is
       reporting. */
    const timeline = useMemo(() => {
        const rows = shown.map((entry) => ({ key: entry.id, entry: entry }));
        if (ticket?.updatedDate) {
            rows.push({ key: "last-updated", entry: null });
        }

        const timeOf = (row) =>
            moment(
                row.entry ? row.entry.createdDate : ticket.updatedDate,
                API_DATE_FORMAT
            ).valueOf() || 0;

        /* the API already hands these over newest first, so a stable sort keeps
           same-minute entries in the order it worked out from full seconds */
        return rows.sort((one, two) => {
            const byDate = timeOf(two) - timeOf(one);
            if (byDate) return byDate;
            if (!one.entry) return -1;
            if (!two.entry) return 1;
            return 0;
        });
    }, [shown, ticket]);

    if (!isOpen) return null;

    return (
        <CustomisedModal
            modalView
            customClass="vp-modal"
            bodyClass="vp-tk-mbody"
            modalName={`Activities - #${ticket?.no}`}
            onModalDismiss={onClose}
        >
            {/* the trail mixes two kinds of entry - this picks one of them out.
                The ticket's own milestones stay put either way, they are what
                the rest is read against. */}
            <div className="vp-tk-hfilter">
                {FILTERS.map((option) => (
                    <button
                        type="button"
                        key={option.key}
                        className={filter === option.key ? "on" : ""}
                        onClick={() => setFilter(option.key)}
                    >
                        {option.label}
                        <i>{counts[option.key]}</i>
                    </button>
                ))}
            </div>

            {/* newest first - "Last updated", every status change with the
                comment behind it, and every forward with where it went */}
            {timeline.map(({ key, entry }) =>
                !entry ? (
                    <div className="vp-tk-hitem" key={key}>
                        <span className="vp-tk-hdot" />
                        <div className="ht">
                            <b>Last updated</b>
                            {ticket?.modifierName ? ` by ${ticket.modifierName}` : ""}
                            <div className="hd">{longStamp(ticket.updatedDate)}</div>
                        </div>
                    </div>
                ) : entry.kind === "forward" ? (
                    <div className="vp-tk-hitem" key={entry.id}>
                        <span className="vp-tk-hdot b" />
                        <div className="ht">
                            <b>Ticket forwarded</b>
                            {entry.addedBy ? ` by ${entry.addedBy}` : ""}
                            <div className="hd">
                                To: {entry.toEmail}
                                {entry.otherEmails ? `, ${entry.otherEmails}` : ""}
                            </div>
                            <div className="hd">{longStamp(entry.createdDate)}</div>
                        </div>
                    </div>
                ) : (
                    <div className="vp-tk-hitem" key={entry.id}>
                        <span className={`vp-tk-hdot ${statusSuffix(entry.status) === "res" ? "g" : "a"}`} />
                        <div className="ht">
                            Status changed to <b>{entry.status || "—"}</b>
                            {entry.addedBy ? ` by ${entry.addedBy}` : ""}
                            <div className="hc">{entry.comment}</div>
                            {entry.addCC || entry.addBCC ? (
                                <div className="hd">
                                    {entry.addCC ? `CC: ${entry.addCC}` : ""}
                                    {entry.addCC && entry.addBCC ? " · " : ""}
                                    {entry.addBCC ? `BCC: ${entry.addBCC}` : ""}
                                </div>
                            ) : null}
                            <div className="hd">{longStamp(entry.createdDate)}</div>
                        </div>
                    </div>
                )
            )}

            {!loading && shown.length === 0 && (
                <div className="vp-tk-hitem">
                    <span className="vp-tk-hdot gr" />
                    <div className="ht">
                        <div className="hd">
                            {filter === "forward"
                                ? "This ticket has not been forwarded yet."
                                : "No status changes on this ticket yet."}
                        </div>
                    </div>
                </div>
            )}

            {loading && comments.length === 0 && (
                <div className="vp-tk-hitem">
                    <span className="vp-tk-hdot" />
                    <div className="ht">
                        <div className="hd">Loading status changes…</div>
                    </div>
                </div>
            )}

            <div className="vp-tk-hitem">
                <span className="vp-tk-hdot" />
                <div className="ht">
                    Priority <b>{ticket?.priority || "—"}</b>
                </div>
            </div>

            {/* the oldest event there is, so it closes the list */}
            <div className="vp-tk-hitem">
                <span className="vp-tk-hdot gr" />
                <div className="ht">
                    <b>Ticket created</b> by {ticket?.from}
                    <div className="hd">{longStamp(ticket?.createdDate)}</div>
                </div>
            </div>

        </CustomisedModal>
    );
};

export default ActivitiesModal;
